import React from "react";

/*
 * Buttons are theme-aware by construction: the light theme renders each accent
 * as a dark fill (white label), the dark theme as a bright fill (ink label),
 * so contrast holds either way. `dark:` here keys off <html data-theme>.
 */
const VARIANTS = {
  primary:
    "btn-shine hover:-translate-y-px bg-cyan-600 text-white shadow-sm hover:bg-cyan-500 dark:bg-cyan-400 dark:text-on-dark dark:hover:bg-cyan-300 dark:shadow-[0_8px_24px_-10px_rgb(var(--c-cyan-400)/.65)]",
  secondary:
    "border border-line-strong bg-surface-3 text-hi hover:border-slate-600/70 hover:bg-slate-700/40",
  outline:
    "border border-line-strong bg-transparent text-mid hover:border-cyan-500/60 hover:bg-cyan-500/[.08] hover:text-hi",
  ghost:
    "bg-transparent text-low hover:bg-slate-700/35 hover:text-hi",
  danger:
    "btn-shine hover:-translate-y-px bg-rose-600 text-white hover:bg-rose-500 dark:bg-rose-600 dark:hover:bg-rose-500",
  success:
    "btn-shine hover:-translate-y-px bg-emerald-600 text-white hover:bg-emerald-500 dark:bg-emerald-500 dark:text-on-dark dark:hover:bg-emerald-400",
  warning:
    "btn-shine hover:-translate-y-px bg-amber-600 text-white hover:bg-amber-500 dark:bg-amber-400 dark:text-on-dark dark:hover:bg-amber-300",
  info:
    "btn-shine hover:-translate-y-px bg-blue-600 text-white hover:bg-blue-500",
  purple:
    "btn-shine hover:-translate-y-px bg-violet-600 text-white hover:bg-violet-500",
  orange:
    "btn-shine hover:-translate-y-px bg-orange-600 text-white hover:bg-orange-500",
};

const SIZES = {
  xs: "h-7 gap-1.5 rounded-lg px-2.5 text-2xs",
  sm: "h-8 gap-1.5 rounded-lg px-3 text-xs",
  md: "h-10 gap-2 rounded-xl px-4 text-sm",
  lg: "h-12 gap-2.5 rounded-xl px-6 text-base",
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  icon,
  iconPosition = "left",
  className = "",
  ...props
}) {
  return (
    <button
      className={`relative inline-flex select-none items-center justify-center whitespace-nowrap font-semibold
        transition-[background-color,border-color,color,transform,box-shadow] duration-150
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas
        active:scale-[.97] disabled:pointer-events-none disabled:opacity-45
        ${VARIANTS[variant] || VARIANTS.primary} ${SIZES[size] || SIZES.md} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {!loading && icon && iconPosition === "left" && <span className="shrink-0">{icon}</span>}
      {children}
      {!loading && icon && iconPosition === "right" && <span className="shrink-0">{icon}</span>}
    </button>
  );
}

const ICON_SIZES = { xs: "h-7 w-7", sm: "h-8 w-8", md: "h-10 w-10", lg: "h-12 w-12" };

export function IconButton({ icon, variant = "ghost", size = "md", className = "", ...props }) {
  return (
    <Button variant={variant} size={size} className={`!px-0 ${ICON_SIZES[size] || ICON_SIZES.md} ${className}`} {...props}>
      {icon}
    </Button>
  );
}

export function ButtonGroup({ children, className = "" }) {
  const items = React.Children.toArray(children);
  return (
    <div className={`inline-flex overflow-hidden rounded-xl border border-line-strong ${className}`}>
      {items.map((child, index) =>
        React.cloneElement(child, {
          key: child.key ?? index,
          className: `${child.props.className || ""} !rounded-none border-0 ${
            index < items.length - 1 ? "border-r border-line-strong" : ""
          }`,
        }),
      )}
    </div>
  );
}
